/**
 * Coarse-mobile splat budget for Volugraph embed, applied before GSplatResource GPU upload.
 *
 * Deleting splats punches holes in the model, so the order of preference is:
 *   1. Strip spherical harmonic bands (colour detail) — keeps every splat.
 *   2. Only when still over budget, drop the least visually significant splats.
 *
 * Config: window.__VOLUGRAPH_SUPER_SPLAT_CONFIG__
 *   volugraphMobileSplatCap  — post-decode splat ceiling (OOM guard).
 *   volugraphMobileStripSh   — strip f_rest_* SH bands on mobile.
 */

import { GSplatData } from 'playcanvas';

/** Typed-array backing store for one vertex property; index signature must stay writable. */
type SplatStorage = {
    length: number;
    [index: number]: number;
};

type VertexProperty = {
    type: string;
    name: string;
    storage?: SplatStorage;
    byteSize: number;
};

/** High-order SH coefficients; `f_dc_*` base colour is always kept. */
const SH_REST_PROPERTY = /^f_rest_\d+$/;

/** Bin count for the importance cutoff search — avoids sorting ~1M indices. */
const IMPORTANCE_BINS = 2048;

function vertexProperties(gsplatData: GSplatData): VertexProperty[] | null {
    const vertex = gsplatData.getElement('vertex') as { properties?: VertexProperty[] } | undefined;
    if (!vertex?.properties?.length) return null;
    return vertex.properties;
}

function rebuildFromProperties(
    gsplatData: GSplatData,
    count: number,
    properties: VertexProperty[]
): GSplatData {
    return new GSplatData(
        [{ name: 'vertex', count, properties }] as never,
        gsplatData.comments ?? []
    );
}

/**
 * Drop SH rest bands. PlayCanvas infers SH degree from the first missing `f_rest_*` column,
 * so removing them all leaves flat `f_dc_*` colour. Storage for kept props is reused, not copied.
 */
export function stripSphericalHarmonicsFromGSplatData(gsplatData: GSplatData): GSplatData {
    const properties = vertexProperties(gsplatData);
    if (!properties) return gsplatData;
    const kept = properties.filter(prop => !SH_REST_PROPERTY.test(prop.name));
    if (kept.length === properties.length) return gsplatData;
    return rebuildFromProperties(gsplatData, gsplatData.numSplats, kept);
}

function gatherRows(gsplatData: GSplatData, indices: Uint32Array): GSplatData | null {
    const properties = vertexProperties(gsplatData);
    if (!properties) return null;
    const target = indices.length;
    const newProps = properties.map((prop) => {
        if (!prop.storage) {
            return { type: prop.type, name: prop.name, storage: prop.storage, byteSize: prop.byteSize };
        }
        const src = prop.storage;
        const Ctor = src.constructor as new (length: number) => SplatStorage;
        const dst = new Ctor(target);
        for (let k = 0; k < target; k++) {
            dst[k] = src[indices[k]!]!;
        }
        return {
            type: prop.type,
            name: prop.name,
            storage: dst,
            byteSize: prop.byteSize,
        };
    });
    return rebuildFromProperties(gsplatData, target, newProps as VertexProperty[]);
}

/**
 * Uniform index subsample. Retained as a fallback for tables without opacity/scale columns —
 * on Morton-ordered data this removes splats in spatial stripes, which is what causes holes.
 */
export function subsampleGSplatDataUniform(gsplatData: GSplatData, cap: number): GSplatData {
    const n = gsplatData.numSplats;
    const target = Math.min(Math.floor(cap), n);
    if (n <= target || target <= 0) {
        return gsplatData;
    }
    const indices = new Uint32Array(target);
    for (let k = 0; k < target; k++) {
        indices[k] = Math.min(n - 1, Math.floor((k * n) / target));
    }
    return gatherRows(gsplatData, indices) ?? gsplatData;
}

function readProp(gsplatData: GSplatData, name: string): ArrayLike<number> | null {
    const storage = gsplatData.getProp(name) as ArrayLike<number> | undefined;
    return storage && typeof storage.length === 'number' ? storage : null;
}

/** Opacity is logit-encoded in PLY/SOG, but some sources store linear alpha. */
function opacityIsLogitEncoded(opacity: ArrayLike<number>, n: number): boolean {
    const step = Math.max(1, Math.floor(n / 512));
    for (let i = 0; i < n; i += step) {
        const v = opacity[i]!;
        if (v < 0 || v > 1) return true;
    }
    return false;
}

/**
 * Screen-coverage proxy: alpha × area of the two largest axes. Ranking by this keeps the
 * large, opaque splats that fill surfaces and discards tiny or near-transparent ones.
 */
function computeImportance(gsplatData: GSplatData): Float32Array | null {
    const n = gsplatData.numSplats;
    const opacity = readProp(gsplatData, 'opacity');
    const s0 = readProp(gsplatData, 'scale_0');
    const s1 = readProp(gsplatData, 'scale_1');
    const s2 = readProp(gsplatData, 'scale_2');
    if (!opacity || !s0 || !s1 || !s2) return null;

    const logit = opacityIsLogitEncoded(opacity, n);
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        const raw = opacity[i]!;
        const alpha = logit ? 1 / (1 + Math.exp(-raw)) : raw;
        const e0 = Math.exp(s0[i]!);
        const e1 = Math.exp(s1[i]!);
        const e2 = Math.exp(s2[i]!);
        // Ignore the thinnest axis: splats are flat discs, so the other two carry the coverage.
        const min = Math.min(e0, Math.min(e1, e2));
        const area = (e0 * e1 * e2) / (min > 0 ? min : 1);
        const score = alpha * area;
        out[i] = Number.isFinite(score) ? score : 0;
    }
    return out;
}

/**
 * Indices of the `target` most significant splats, emitted in ascending order so the
 * result keeps its Morton locality (important for render performance).
 */
function selectTopIndices(importance: Float32Array, target: number): Uint32Array | null {
    const n = importance.length;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < n; i++) {
        const v = importance[i]!;
        if (v < min) min = v;
        if (v > max) max = v;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;

    const scale = IMPORTANCE_BINS / (max - min);
    const binOf = (v: number) => {
        const b = Math.floor((v - min) * scale);
        return b < 0 ? 0 : b >= IMPORTANCE_BINS ? IMPORTANCE_BINS - 1 : b;
    };

    const counts = new Uint32Array(IMPORTANCE_BINS);
    for (let i = 0; i < n; i++) {
        counts[binOf(importance[i]!)]++;
    }

    let cumulative = 0;
    let cutoffBin = 0;
    for (let b = IMPORTANCE_BINS - 1; b >= 0; b--) {
        cumulative += counts[b]!;
        if (cumulative >= target) {
            cutoffBin = b;
            break;
        }
    }

    // Bins above the cutoff are kept wholesale; the cutoff bin itself is filled until target.
    let remainingFromCutoff = target - (cumulative - counts[cutoffBin]!);
    const out = new Uint32Array(target);
    let w = 0;
    for (let i = 0; i < n && w < target; i++) {
        const b = binOf(importance[i]!);
        if (b > cutoffBin) {
            out[w++] = i;
        } else if (b === cutoffBin && remainingFromCutoff > 0) {
            out[w++] = i;
            remainingFromCutoff--;
        }
    }
    if (w === 0) return null;
    return w === target ? out : out.slice(0, w);
}

/** Keep the most visually significant splats, preserving coverage instead of striping holes. */
export function subsampleGSplatDataByImportance(gsplatData: GSplatData, cap: number): GSplatData {
    const n = gsplatData.numSplats;
    const target = Math.min(Math.floor(cap), n);
    if (n <= target || target <= 0) {
        return gsplatData;
    }
    const importance = computeImportance(gsplatData);
    if (!importance) {
        return subsampleGSplatDataUniform(gsplatData, target);
    }
    const indices = selectTopIndices(importance, target);
    if (!indices) {
        return subsampleGSplatDataUniform(gsplatData, target);
    }
    return gatherRows(gsplatData, indices) ?? gsplatData;
}

/**
 * Apply the configured mobile budget: strip SH first, then prune only if still over the cap.
 */
export function applyVolugraphMobileSplatCapIfConfigured(gsplatData: GSplatData): GSplatData {
    const w = window as unknown as {
        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: {
            volugraphMobileSplatCap?: number;
            volugraphMobileStripSh?: boolean;
        };
    };
    const config = w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
    const cap = config?.volugraphMobileSplatCap;
    const stripSh = config?.volugraphMobileStripSh === true;
    const hasCap = typeof cap === 'number' && Number.isFinite(cap) && cap > 0;
    if (!hasCap && !stripSh) {
        return gsplatData;
    }

    const originalCount = gsplatData.numSplats;
    let out = gsplatData;
    let shStripped = false;

    if (stripSh) {
        const withoutSh = stripSphericalHarmonicsFromGSplatData(out);
        shStripped = withoutSh !== out;
        out = withoutSh;
    }

    const cappedN = hasCap ? Math.floor(cap as number) : originalCount;
    if (hasCap && originalCount > cappedN) {
        const pruned = subsampleGSplatDataByImportance(out, cappedN);
        if (pruned !== out && pruned.numSplats < originalCount) {
            out = pruned;
        }
    }

    if (out === gsplatData) {
        return gsplatData;
    }

    try {
        window.dispatchEvent(
            new CustomEvent('volugraph-mobile-cap-applied', {
                detail: {
                    originalCount,
                    cappedCount: out.numSplats,
                    shStripped,
                    splatsDropped: originalCount - out.numSplats,
                },
            })
        );
    } catch {
        /* ignore */
    }
    const isDevelopment =
        typeof process !== 'undefined' &&
        process.env?.NODE_ENV === 'development';
    if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.info(
            '[Volugraph] Mobile splat budget applied',
            originalCount,
            '->',
            out.numSplats,
            shStripped ? '(SH stripped)' : ''
        );
    }
    return out;
}
